import React from 'react';
import { Helmet } from 'react-helmet-async';
import { HelpCircle } from 'lucide-react';
import { SEO } from '../components/SEO';

const faqs = [
  {
    question: "How accurate are the conversions?",
    answer: "Our conversions are highly accurate, using precise conversion factors with up to 15 decimal places. You can adjust the display precision as needed."
  },
  {
    question: "Can I save my favorite conversions?",
    answer: "Yes! Click the star icon next to any conversion to save it to your favorites. Your favorites are stored locally and will be available the next time you visit."
  },
  {
    question: "How do I use the Smart Calculator?",
    answer: "The Smart Calculator allows you to perform complex calculations with mixed units. Simply enter your expression (e.g., '5m + 30cm') and get instant results with step-by-step explanations."
  },
  {
    question: "Are the conversions available offline?",
    answer: "Currently, you need an internet connection to use the converter. We're working on an offline mode that will be available in a future update."
  },
  {
    question: "How do I report an error?",
    answer: "If you notice any conversion errors or have suggestions, please contact us through our support page or email aristomenis.georgiopoulos@gmail.com."
  },
  {
    question: "Can I embed the converter on my website?",
    answer: "We're developing an embeddable widget. For now, you can link directly to specific conversion pages using our URL format: /category/from-to."
  },
  {
    question: "What's the difference between Metric and Imperial?",
    answer: "The metric system is based on powers of 10 and is used worldwide. The imperial system uses different conversion factors and is primarily used in the US and a few other countries."
  },
  {
    question: "How do I use the conversion tables?",
    answer: "Our conversion tables show relationships between different units. Find your starting unit in the left column, then read across to find the converted value in other units."
  }
];

function FAQ() {
  return (
    <>
      <SEO 
        title="Frequently Asked Questions About Unit Conversion"
        description="Find answers to common questions about unit conversions, calculator features, and how to use our converter effectively."
        canonicalUrl="https://easyunitconverter.com/faq"
        keywords="unit conversion FAQ, conversion help, calculator guide"
      />
      <div className="min-h-screen bg-gradient-to-b from-gray-50 to-white">
        <Helmet>
          <title>Frequently Asked Questions | Easy Unit Converter</title>
          <meta name="description" content="Find answers to common questions about unit conversions, calculator features, and how to use Easy Unit Converter effectively." />
        </Helmet>

        <main className="max-w-4xl mx-auto px-4 py-12">
          <div className="text-center mb-12">
            <h1 className="text-4xl font-bold text-gray-900 mb-4">
              Frequently Asked Questions
            </h1>
            <p className="text-lg text-gray-600">
              Find answers to common questions about using Easy Unit Converter
            </p>
          </div>

          <div className="space-y-6">
            {faqs.map((faq, index) => (
              <div
                key={index}
                className="bg-white rounded-xl border border-gray-100 shadow-sm overflow-hidden"
              >
                <details className="group">
                  <summary className="flex items-center gap-4 p-6 cursor-pointer list-none">
                    <div className="flex-1 flex items-center gap-4">
                      <HelpCircle className="w-5 h-5 text-blue-600 flex-shrink-0" />
                      <h2 className="text-lg font-medium text-gray-900">
                        {faq.question}
                      </h2>
                    </div>
                    <div className="flex-shrink-0 ml-4">
                      <svg
                        className="w-5 h-5 transform transition-transform group-open:rotate-180"
                        xmlns="http://www.w3.org/2000/svg"
                        fill="none"
                        viewBox="0 0 24 24"
                        stroke="currentColor"
                      >
                        <path
                          strokeLinecap="round"
                          strokeLinejoin="round"
                          strokeWidth={2}
                          d="M19 9l-7 7-7-7"
                        />
                      </svg>
                    </div>
                  </summary>
                  <div className="px-6 pb-6 text-gray-600">
                    {faq.answer}
                  </div>
                </details>
              </div>
            ))}
          </div>
        </main>
      </div>
    </>
  );
}

export default FAQ;